<template>
  <div 
    class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6 hover:shadow-md transition-shadow"
    :class="layout === 'list' ? 'flex flex-row items-center space-x-6 h-32' : 'flex flex-col h-full'"
  >
    
    <div class="flex items-start" :class="layout === 'list' ? 'w-1/3 space-x-4' : 'space-x-4 mb-4'">
      <!-- Logo Container -->
      <div 
        class="rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center border border-slate-200 dark:border-slate-600 flex-shrink-0"
        :class="layout === 'list' ? 'w-12 h-12' : 'w-16 h-16 p-3'"
      >
         <Icon :name="getIconName(pipeline.appId)" class="text-slate-600 dark:text-slate-300" :class="layout === 'list' ? 'w-6 h-6' : 'w-10 h-10'" />
      </div>
      
      <div class="flex-1 min-w-0">
        <h3 class="text-lg font-semibold text-slate-900 dark:text-white truncate">{{ pipeline.name }}</h3>
        
        <div class="flex items-center text-sm text-slate-500 mt-1">
           <Icon :name="pipeline.taskType === 'ingestion' ? 'heroicons:cloud-arrow-down' : 'heroicons:cloud-arrow-up'" class="w-4 h-4 mr-1" />
           <span class="font-medium mr-1">{{ pipeline.appName.toUpperCase() }}</span>
           <span class="opacity-75" v-if="layout !== 'list'">- {{ pipeline.taskType === 'ingestion' ? 'Inbound' : 'Outbound' }}</span>
        </div>

        <div class="mt-2 flex items-center justify-between">
            <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium uppercase"
                :class="{
                    'bg-green-100 text-green-800': pipeline.status === 'HEALTHY',
                    'bg-yellow-100 text-yellow-800': pipeline.status === 'WARNING',
                    'bg-red-100 text-red-800': pipeline.status === 'ERROR'
                }"
            >
                {{ pipeline.status }}
            </span>
            
            <button @click="$emit('view-details', pipeline)" class="text-xs text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-200 font-medium">
                View Details &rarr;
            </button>
        </div>
      </div>
    </div>

    <!-- Active Loading Progress -->
    <div v-if="pipeline.loadingState?.active" class="mb-4">
        <div class="flex justify-between text-xs mb-1">
            <span class="text-slate-600 dark:text-slate-400 font-medium animate-pulse">{{ pipeline.loadingState.stage }}</span>
            <span class="text-slate-500">{{ pipeline.loadingState.progress }}%</span>
        </div>
        <div class="w-full bg-slate-100 dark:bg-slate-700 rounded-full h-1.5">
            <div class="bg-indigo-500 h-1.5 rounded-full transition-all duration-500" :style="{ width: pipeline.loadingState.progress + '%' }"></div>
        </div>
    </div>

    <!-- Stats Row -->
    <div class="grid grid-cols-2 gap-4 mb-4 border-t border-b border-slate-100 dark:border-slate-700/50 py-3">
        <div>
            <div class="text-[10px] uppercase text-slate-400 font-semibold tracking-wider">Loaded Today</div>
            <div class="text-sm font-bold text-slate-700 dark:text-slate-300">
                {{ formatNumber(pipeline.todayStats?.active_events || 0) }} 
                <span v-if="pipeline.todayStats?.errored_events > 0" class="text-red-500 text-[10px] ml-1">({{ pipeline.todayStats.errored_events }} err)</span>
            </div>
        </div>
        <div class="text-right">
            <div class="text-[10px] uppercase text-slate-400 font-semibold tracking-wider">Last Load</div>
            <div class="text-sm font-medium text-slate-600 dark:text-slate-400">
                {{ formatTime(pipeline.todayStats?.last_load_time) }}
            </div>
        </div>
    </div>

    <!-- Chart -->
    <div class="relative" :class="layout === 'list' ? 'flex-1 h-full py-2 ml-4 border-l pl-4 dark:border-slate-700' : 'flex-1 mt-auto h-32'">
        <Bar :data="chartData" :options="chartOptions" :plugins="[dataLabelsPlugin]" />
    </div>

  </div>
</template>

<script setup lang="ts">
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale
} from 'chart.js'
import { Bar } from 'vue-chartjs'

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

// Define props with defaults
const props = withDefaults(defineProps<{
  pipeline: any,
  layout?: 'grid' | 'list'
}>(), {
  layout: 'grid'
})

defineEmits(['view-details'])

const getIconName = (appId: string) => {
    switch(appId) {
        case 'slack': return 'logos:slack-icon'
        case 'salesforce': return 'logos:salesforce'
        case 'hubspot': return 'logos:hubspot'
        case 'stripe': return 'logos:stripe'
        case 'zendesk': return 'logos:zendesk'
        default: return 'heroicons:cube-transparent'
    }
}

const formatNumber = (num: number) => {
  return new Intl.NumberFormat('en-US', { notation: "compact", compactDisplay: "short" }).format(num);
}

const formatTime = (isoString?: string) => {
    if (!isoString) return '-'
    const d = new Date(isoString)
    // Check if today
    const today = new Date()
    if (d.toDateString() === today.toDateString()) {
        return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' })
}

const chartData = computed(() => {
  // Slicing logic based on layout
  // Grid: Show last 7 days
  // List: Show full history (30 days)
  const limit = props.layout === 'grid' ? 7 : 30
  
  // Assuming arrays are oldest -> newest. We want the END of the array.
  const allLabels = props.pipeline.runs.labels
  const allSuccess = props.pipeline.runs.success
  const allError = props.pipeline.runs.error
  
  const labels = allLabels.slice(-limit)
  const success = allSuccess.slice(-limit)
  const error = allError.slice(-limit)

  return {
    labels: labels,
    datasets: [
      {
        label: 'Success',
        backgroundColor: '#10B981', // Emerald
        borderRadius: 2,
        data: success,
        stack: 'stack0'
      },
      {
        label: 'Error',
        backgroundColor: '#EF4444', // Red
        borderRadius: 2,
        data: error,
        stack: 'stack0'
      }
    ]
  }
})

const chartOptions = computed(() => {
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          enabled: true
        }
      },
      scales: {
        y: {
          stacked: true,
          display: true, 
          beginAtZero: true,
          grid: {
            color: 'rgba(200, 200, 200, 0.1)',
            drawTicks: false
          },
          ticks: {
            font: {
                size: 9
            },
            display: props.layout === 'list' // Hide Y label numbers in grid view if it's too cramped, or strictly user requested axis everywhere? User asked for Axes everywhere.
          },
          border: {
              display: false
          }
        },
        x: {
            stacked: true,
            display: true,
            grid: {
                display: false
            },
            ticks: {
                font: {
                    size: 9
                },
                maxRotation: 0,
                autoSkip: true, // Clean up X axis for 30 days
                maxTicksLimit: props.layout === 'grid' ? 7 : 15
            },
            border: {
                display: false
            }
        }
      }
    }
})

const dataLabelsPlugin = {
  id: 'customDataLabels',
  afterDatasetsDraw(chart: any) {
    if (props.layout === 'list') return; // Disable data labels in List view to avoid cluttering 30 bars

    const { ctx } = chart
    
    chart.data.datasets.forEach((dataset: any, i: number) => {
        const meta = chart.getDatasetMeta(i)
        if (meta.hidden) return

        meta.data.forEach((element: any, index: number) => {
            const data = dataset.data[index]
            if (data > 0) { 
                ctx.fillStyle = i === 0 ? '#065F46' : '#991B1B' 
                ctx.font = 'bold 9px sans-serif'
                ctx.textAlign = 'center'
                ctx.textBaseline = 'bottom'
                const yPos = element.y - 2 // Position slightly above? Or stick to internal logic
                
                // Drawing roughly at the top of the bar segment
                ctx.fillText(data, element.x, element.y + 10)
            }
        })
    })
  }
}
</script>
